import { HttpErrorResponse } from '@angular/common/http';

/**
 * Lectura del cuerpo de error del backend (`ApiResponse` / `ErrorResponse`), compartida por el
 * `errorInterceptor` y los estados de carga de las pantallas.
 *
 * - 400/403/404/409/422 traen un `message` ya localizado por `Accept-Language` (p. ej.
 *   `error.vet.appointment.scheduleByReception`, el "no encontrado" vet con `RESOURCE_NOT_FOUND`).
 * - El 500 trae un mensaje genérico y el id de correlación en el campo `errorId` (y en la cabecera
 *   `X-Error-Id`, que el CORS puede no exponer: por eso se lee primero el cuerpo).
 */

/** Estados cuyo `message` del backend se muestra tal cual (nunca el de un 5xx). */
const USER_FACING_STATUSES: readonly number[] = [400, 403, 404, 409, 422];

/** Código del 409 por versión optimista desactualizada (`@Version`). */
export const STALE_VERSION_CODE = 'STALE_VERSION';

/** Campos del cuerpo con el id de correlación de un error. */
const ERROR_ID_FIELDS = ['errorId', 'correlationId', 'traceId', 'requestId'] as const;
/** Cabeceras con el id de correlación (solo legibles si el CORS las expone). */
const ERROR_ID_HEADERS = ['X-Error-Id', 'X-Correlation-Id', 'X-Request-Id', 'X-Trace-Id'] as const;

function bodyOf(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof HttpErrorResponse)) return null;
  const body: unknown = error.error;
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Mensaje del backend para el usuario (solo en 4xx de negocio); `null` si no viene. */
export function backendMessageOf(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse) || !USER_FACING_STATUSES.includes(error.status)) return null;
  return text(bodyOf(error)?.['message']);
}

/** Código del cuerpo de error (`RESOURCE_NOT_FOUND`, `STALE_VERSION`, …); `null` si no viene. */
export function errorCodeOf(error: unknown): string | null {
  return text(bodyOf(error)?.['code']);
}

/** 409 por versión optimista: otro usuario modificó el registro entre la lectura y el guardado. */
export function isStaleVersionError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 409 && errorCodeOf(error) === STALE_VERSION_CODE;
}

/**
 * Id de correlación del error para citarlo al soporte: primero el cuerpo (`errorId`), después las
 * cabeceras. Leer una cabecera no expuesta por CORS devuelve `null`, nunca falla.
 */
export function errorIdOf(error: unknown): string | null {
  const body = bodyOf(error);
  for (const field of ERROR_ID_FIELDS) {
    const value = text(body?.[field]);
    if (value) return value;
  }
  if (error instanceof HttpErrorResponse) {
    for (const header of ERROR_ID_HEADERS) {
      const value = text(error.headers?.get(header));
      if (value) return value;
    }
  }
  return null;
}

/** Detalle mostrable de un error de carga: mensaje del backend (4xx) o id de correlación (5xx). */
export interface HttpErrorDetail {
  message: string | null;
  errorId: string | null;
}

/** `null` si el error no trae nada útil que mostrar además del mensaje genérico. */
export function httpErrorDetail(error: unknown): HttpErrorDetail | null {
  if (!(error instanceof HttpErrorResponse)) return null;
  const message = backendMessageOf(error);
  const errorId = error.status >= 500 ? errorIdOf(error) : null;
  return message || errorId ? { message, errorId } : null;
}
